import * as Updates from 'expo-updates'; // ✅ For OTA update checks
import NetInfo from '@react-native-community/netinfo';
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { authApi } from '../services/api';
import websocketService from '../services/websocket';
import { onSessionInvalidated } from '../utils/authEvents';
import logger from '../utils/logger';
import { clearAllUserData, platformStorage, storeTokens } from '../utils/storage';

// ⚠️ PERMANENT SESSION - NEVER EXPIRES
// Token: 30 days, Refresh: 365 days, No auto-refresh
// User stays logged in until manual logout or app uninstall
// Token only refreshes reactively on 401 errors (not proactively)
// Modified: Jan 29, 2026 - True permanent session

export interface Role {
  name: string;
  nameAr: string;
  scope: 'platform' | 'organization' | 'site' | 'zone';
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  orgId?: string;
  roles: Role[];
  organization?: {
    id: string;
    name: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  pending2FA: boolean; // NEW: Flag to indicate user needs 2FA verification
  login: (email: string, password: string) => Promise<any>; // Returns tempToken if 2FA required
  verify2FA: (
    tempToken: string,
    code: string,
    method: 'totp' | 'email_otp' | 'backup_code',
  ) => Promise<any>; // Complete 2FA verification
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateUser: (updatedUser: Partial<User>) => void;
  setPending2FA: (pending: boolean) => void; // NEW: Set 2FA pending flag
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(false); // NEW: Track 2FA pending state
  const [hasValidSession, setHasValidSession] = useState(false); // ✅ FIX: Track if tokens exist, independent of user state
  const tokenRefreshIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function endInvalidSession(reason: string) {
    logger.warn('🔒 [AUTH] Ending invalid local session:', reason);

    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }

    websocketService.disconnect();
    await clearAllUserData();
    setPending2FA(false);
    setHasValidSession(false);
    setUser(null);
    setIsLoading(false);
  }

  useEffect(() => {
    return onSessionInvalidated((reason) => {
      logger.warn('🔒 [AUTH] Session invalidated by API client:', reason);
      void endInvalidSession(reason);
    });
  }, []);

  // ✅ FIX: Restore user from cache on App Resume (iOS memory cleanup protection)
  // When iOS cleans app memory in background, user state is lost
  // On resume, reload user from cache WITHOUT calling API
  // Token refresh happens automatically via API interceptor on 401
  useEffect(() => {
    const subscription = AppState.addEventListener(
      'change',
      async (nextAppState: AppStateStatus) => {
        logger.log(`📱 [AUTH] App state changed to: ${nextAppState}`);

        // When app comes back to foreground
        if (nextAppState === 'active') {
          // Check if we have tokens but no user (memory was cleaned)
          const token = await platformStorage.getItem('access_token');

          if (token && !user) {
            logger.warn('⚠️ [AUTH] App resumed with token but no user - restoring from cache...');

            // Restore user from cache (don't call API)
            const cachedUserData = await platformStorage.getItem('user_data');
            if (cachedUserData) {
              try {
                const cachedUser = JSON.parse(cachedUserData);
                setUser(cachedUser);
                setHasValidSession(true);
                logger.log('✅ [AUTH] User restored from cache on app resume:', {
                  userId: cachedUser.id,
                  orgId: cachedUser.orgId,
                });
              } catch (error) {
                logger.error('❌ [AUTH] Failed to parse cached user on resume:', error);
              }
            } else {
              // Token exists but no cache - keep session flag so user isn't redirected to login
              setHasValidSession(true);
            }
          } else if (user) {
            logger.log('✅ [AUTH] App resumed - user still in memory (permanent session)');
          } else {
            logger.log('ℹ️ [AUTH] App resumed - no user session');
          }
        }
      },
    );

    return () => {
      subscription.remove();
    };
  }, [user]);

  // Refresh access token with retry
  const refreshToken = async (retryCount = 0): Promise<boolean> => {
    const MAX_RETRIES = 2;
    const attemptNum = retryCount + 1;

    logger.log(`🔄 [AUTH] refreshToken() called (attempt ${attemptNum}/${MAX_RETRIES + 1})`);

    try {
      const refreshTokenValue = await platformStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        logger.warn('⚠️ [AUTH] No refresh token in storage');
        return false;
      }

      logger.log('📞 [AUTH] Calling refresh endpoint...');
      const startTime = Date.now();

      // Call refresh endpoint
      const response = await authApi.refresh(refreshTokenValue);

      const duration = Date.now() - startTime;
      logger.log(`✅ [AUTH] Refresh endpoint responded in ${duration}ms`);

      // Store new tokens using helper function
      await storeTokens({
        accessToken: response.tokens.access,
        refreshToken: response.tokens.refresh || refreshTokenValue,
      });

      logger.log('✅ [AUTH] Tokens stored successfully');

      // تحديث الـ WebSocket بالتوكن الجديد (بدون مسح المستمعين)
      websocketService.reconnectWithToken(response.tokens.access);

      return true;
    } catch (error: any) {
      const errorInfo = {
        attempt: attemptNum,
        message: error.message,
        code: error.code,
        status: error.response?.status,
      };
      logger.error('❌ [AUTH] Token refresh failed:', errorInfo);

      // Check if it's a network error
      const isNetworkError =
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.code === 'ENOTFOUND' ||
        error.code === 'ETIMEDOUT' ||
        !error.response;

      // Retry on network errors
      if (isNetworkError && retryCount < MAX_RETRIES) {
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
        logger.warn(
          `Retrying token refresh in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`,
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        return refreshToken(retryCount + 1);
      }

      // On auth errors (401, 403) or max retries, keep user logged in
      const isAuthError = error.response?.status === 401 || error.response?.status === 403;
      if (isAuthError) {
        await endInvalidSession('refresh token rejected');
      } else if (retryCount >= MAX_RETRIES) {
        logger.warn('Token refresh failed after retries - continuing with cached data');
      }

      return false;
    }
  };

  // ⚠️ PERMANENT SESSION - NO AUTO REFRESH
  // Token refresh only happens on 401 errors (reactive, not proactive)
  // User stays logged in until manual logout or app uninstall
  // Modified: Jan 29, 2026
  useEffect(() => {
    if (user) {
      logger.log('✅ [AUTH] Permanent session mode - no auto-refresh timer');
      logger.log('📌 [AUTH] Token will only refresh on demand (when 401 occurs)');
    }
    // No interval - token lasts 30 days, refresh lasts 365 days
  }, [user]);

  // 🌐 Network Recovery: refresh token + reconnect WebSocket when internet returns
  // مع debounce لتجنب استدعاءات متكررة عند تبديل الشبكة (WiFi ↔ Cellular)
  useEffect(() => {
    let wasOffline = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = NetInfo.addEventListener(async (state) => {
      const connected = state.isConnected && state.isInternetReachable !== false;

      if (!connected) {
        wasOffline = true;
        if (debounceTimer) {
          clearTimeout(debounceTimer);
          debounceTimer = null;
        }
        logger.warn('📡 [AUTH] Network lost - will refresh token when reconnected');
        return;
      }

      // Network just came back after being offline — debounce 2s for stability
      if (wasOffline && connected && user) {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          wasOffline = false;
          logger.log('📡 [AUTH] Network restored (stable 2s) - refreshing token...');

          // Refresh token so queued/new requests don't hit 401
          const refreshed = await refreshToken();
          if (refreshed) {
            logger.log('✅ [AUTH] Token refreshed after network recovery');
            // Reconnect WebSocket with fresh token
            const freshToken = await platformStorage.getItem('access_token');
            if (freshToken && user.orgId) {
              websocketService.reconnectWithToken(freshToken);
            }
          } else {
            logger.warn(
              '⚠️ [AUTH] Token refresh failed after network recovery - will retry on next 401',
            );
          }
        }, 2000);
      } else if (wasOffline && connected) {
        wasOffline = false;
      }
    });

    return () => {
      unsubscribe();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [user]);

  const checkAuth = async () => {
    try {
      // ✅ Check if there's a pending 2FA session (don't reset it!)
      const pending2FAData = await platformStorage.getItem('pending_2fa');
      if (pending2FAData) {
        logger.log('🔐 [AUTH] checkAuth: Found pending 2FA session, keeping pending2FA=true');
        setPending2FA(true);
        setIsLoading(false);
        return; // Don't proceed with normal auth check during 2FA
      } else {
        // No pending 2FA - reset flag
        setPending2FA(false);
      }

      const token = await platformStorage.getItem('access_token');
      if (!token) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      // Token exists → mark session valid immediately (prevents redirect to login)
      setHasValidSession(true);

      // Try to get cached user data first
      const cachedUserData = await platformStorage.getItem('user_data');
      let cachedUser = null;

      if (cachedUserData) {
        try {
          cachedUser = JSON.parse(cachedUserData);

          // Validate cached user has required fields
          const hasValidOrganization =
            cachedUser?.organization &&
            typeof cachedUser.organization === 'object' &&
            cachedUser.organization.id &&
            cachedUser.organization.name;

          // Platform Admin has orgId = null — that's valid, don't reject their cache
          const isPlatformAdminUser = cachedUser?.roles?.some(
            (r: any) =>
              (typeof r === 'object' && r !== null && r.scope === 'platform') ||
              r === 'مدير المنصة' ||
              r === 'Platform Admin',
          );
          const hasValidOrgOrPlatform =
            isPlatformAdminUser || (cachedUser?.orgId && hasValidOrganization);

          if (!cachedUser || !cachedUser.id || !hasValidOrgOrPlatform) {
            logger.warn('Cached user data is incomplete:', {
              hasUser: !!cachedUser,
              hasId: !!cachedUser?.id,
              hasOrgId: !!cachedUser?.orgId,
              isPlatformAdmin: !!isPlatformAdminUser,
              hasOrganization: !!cachedUser?.organization,
            });
            await platformStorage.removeItem('user_data');
            cachedUser = null;
          } else {
            // Temporarily set cached user while fetching fresh data
            setUser(cachedUser);
            logger.log('User data loaded from cache:', {
              userId: cachedUser.id,
              orgId: cachedUser.orgId,
              orgName: cachedUser.organization.name,
            });
          }
        } catch (parseError) {
          logger.error('Failed to parse cached user data:', parseError);
          await platformStorage.removeItem('user_data');
          cachedUser = null;
        }
      }

      // Try to fetch fresh user data from server with retry
      let retryCount = 0;
      const MAX_RETRIES = 1; // ⬇️ Reduced from 2 to 1 for faster response
      let lastError: any = null;

      logger.log(`🔍 [AUTH] checkAuth() fetching user data from server...`);

      while (retryCount <= MAX_RETRIES) {
        try {
          const attemptNum = retryCount + 1;
          logger.log(
            `📞 [AUTH] Attempt ${attemptNum}/${MAX_RETRIES + 1} - calling getCurrentUser()`,
          );

          const userData = await authApi.getCurrentUser();
          setUser(userData);
          setHasValidSession(true);

          // Update cached user data
          await platformStorage.setItem('user_data', JSON.stringify(userData));

          logger.log('✅ [AUTH] User data refreshed from server:', {
            userId: userData.id,
            orgId: userData.orgId,
            hasOrganization: !!userData.organization,
            orgName: userData.organization?.name,
          });
          return; // Success - exit function
        } catch (apiError: any) {
          lastError = apiError;

          const errorInfo = {
            attempt: retryCount + 1,
            message: apiError.message,
            code: apiError.code,
            status: apiError.response?.status,
          };
          logger.warn('⚠️ [AUTH] API error in checkAuth:', errorInfo);

          // Determine if this is a network error or auth error
          const isNetworkError =
            apiError.code === 'ECONNABORTED' ||
            apiError.code === 'ERR_NETWORK' ||
            apiError.code === 'ENOTFOUND' ||
            apiError.code === 'ETIMEDOUT' ||
            apiError.message?.toLowerCase().includes('timeout') ||
            apiError.message?.toLowerCase().includes('network') ||
            apiError.message?.toLowerCase().includes('connection') ||
            !apiError.response; // No response means network issue

          const isAuthError =
            apiError.response?.status === 401 || apiError.response?.status === 403;

          if (isAuthError) {
            // ✅ FIX: Don't logout immediately - try refresh first (PERMANENT SESSION)
            logger.warn('⚠️ [AUTH] Authentication error (401/403) - attempting token refresh...');

            // Try manual refresh before giving up
            const refreshSuccess = await refreshToken();

            if (refreshSuccess) {
              logger.log('✅ [AUTH] Token refreshed successfully - retrying getCurrentUser...');

              // Retry getCurrentUser with new token
              try {
                const userData = await authApi.getCurrentUser();
                setUser(userData);
                setHasValidSession(true);
                await platformStorage.setItem('user_data', JSON.stringify(userData));
                logger.log('✅ [AUTH] User data fetched successfully after refresh');
                return; // Success - exit checkAuth
              } catch (retryError: any) {
                logger.error('❌ [AUTH] Failed to fetch user even after refresh:', retryError);
                // Fall through to use cached data below
              }
            }

            await endInvalidSession('auth check refresh failed');
            return;
          }

          if (isNetworkError && retryCount < MAX_RETRIES) {
            // Network error - retry with exponential backoff
            const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
            logger.warn(
              `Network error - retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`,
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            retryCount++;
          } else {
            // Max retries reached or non-network error
            break;
          }
        }
      }

      // If we get here, all retries failed
      logger.error('Failed to fetch user after retries:', {
        error: lastError?.message,
        code: lastError?.code,
        status: lastError?.response?.status,
        hasCachedUser: !!cachedUser,
      });

      // ✅ FIX: ALWAYS use cached data if available, NEVER logout on network errors
      if (cachedUser) {
        logger.warn('✅ [AUTH] Using cached user data after failed retries (permanent session):', {
          userId: cachedUser.id,
          orgId: cachedUser.orgId,
          hasOrganization: !!cachedUser.organization,
        });
        setUser(cachedUser);
        setHasValidSession(true);
      } else {
        // ✅ FIX: tokens exist but no cache - keep session alive, DO NOT set user=null
        logger.warn('⚠️ [AUTH] No cached data but tokens exist - keeping session alive');
        // Don't call clearTokens() or setUser(null)
        setHasValidSession(true); // tokens exist → stay logged in
      }
    } catch (error: any) {
      logger.error('checkAuth failed:', error);
      // Critical error - but don't logout, keep session alive
      logger.warn('Critical error in checkAuth - keeping user logged in');
      if (user) setHasValidSession(true); // keep existing session
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      logger.log(`🔐 [AUTH] Login attempt for: ${email}`);

      const startTime = Date.now();
      const response = await authApi.login(email, password);
      const duration = Date.now() - startTime;

      logger.log(`✅ [AUTH] Login API successful in ${duration}ms`);

      // ✅ Check if 2FA is required
      if (response.requires2FA) {
        logger.log('🔐 [AUTH] 2FA verification required');
        setPending2FA(true);
        setIsLoading(false); // ✅ مهم: خلي الشاشة تفتح بدون loading

        logger.log('✅ [AUTH] 2FA state activated, ready for verification screen');

        // Return tempToken and 2FA info for TwoFactorVerifyScreen
        return {
          tempToken: response.tempToken,
          has2FA: response.has2FA,
          email: response.email,
          requires2FA: true,
        };
      }

      // No 2FA required - complete login
      // Store tokens using helper function
      await storeTokens({
        accessToken: response.tokens.access,
        refreshToken: response.tokens.refresh,
      });
      logger.log('✅ [AUTH] Tokens stored in SecureStore');

      // Store user data
      await platformStorage.setItem('user_data', JSON.stringify(response.user));
      logger.log('✅ [AUTH] User data cached');

      // Set user
      setUser(response.user);
      setHasValidSession(true);

      // Connect to WebSocket for real-time updates
      websocketService.connect(response.user.orgId, response.tokens.access);

      // Register for push notifications in background (don't block login)
      setTimeout(async () => {
        // Skip notifications on web platform
        if (Platform.OS === 'web') {
          logger.log('Skipping push notifications on web platform');
          return;
        }

        try {
          const { default: notificationService } = await import('../services/notificationService');
          const token = await notificationService.registerForPushNotifications();
          if (token) {
            await notificationService.updatePushToken(token);
            logger.log('Push token registered after login');
          }
        } catch (notifError) {
          logger.warn('Failed to register push notifications after login:', notifError);
          // Don't throw - notifications are not critical for login
        }
      }, 2000); // Wait 2 seconds after login

      logger.log('Login successful - permanent session started', {
        userId: response.user.id,
        orgId: response.user.orgId,
      });

      // ✅ Check for OTA updates after successful login (silent background download)
      if (!__DEV__ && Platform.OS !== 'web' && Updates.isEnabled) {
        setTimeout(async () => {
          try {
            logger.log('🔍 [OTA] Checking for updates after login...');
            const update = await Updates.checkForUpdateAsync();

            if (update.isAvailable) {
              logger.log('📥 OTA update available after login, downloading...', {
                updateId: update.manifest?.id,
                timestamp: new Date().toISOString(),
              });

              await Updates.fetchUpdateAsync();
              logger.log('✅ OTA update downloaded after login, will apply on next app restart');
              logger.log('✅ [OTA] Update downloaded successfully after login');
            } else {
              logger.log('✅ [OTA] No updates available after login');
            }
          } catch (otaError) {
            logger.warn('⚠️ [OTA] Failed to check updates after login:', otaError);
            logger.warn('Failed to check OTA updates after login', otaError);
          }
        }, 3000); // Wait 3 seconds after login to not block UI
      }

      // Return access token for legacy compatibility
      return { accessToken: response.tokens.access };
    } catch (error: any) {
      logger.error('Login failed:', error);

      // Handle rate limiting (429)
      if (error.response?.status === 429) {
        const retryAfter = error.response?.headers?.['retry-after'];
        const waitTime = retryAfter ? `${retryAfter} seconds` : 'a few moments';
        throw new Error(`Too many login attempts. Please wait ${waitTime} before trying again.`);
      }

      // Handle network errors
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error(
          'Connection timeout. Make sure the server is running and check your IP address.',
        );
      }
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network')) {
        throw new Error(
          'Network error. Make sure the server is running and you are connected to the network.',
        );
      }

      // Other errors
      throw new Error(error.response?.data?.message || 'Login failed');
    } finally {
      // ✅ نضمن إيقاف Loading في كل الحالات (2FA تم إيقافه يدوياً أعلاه)
      if (!pending2FA) {
        setIsLoading(false);
      }
    }
  };

  const verify2FA = async (
    tempToken: string,
    code: string,
    method: 'totp' | 'email_otp' | 'backup_code',
  ) => {
    try {
      setIsLoading(true);
      logger.log(`🔐 [AUTH] Verifying 2FA code with method: ${method}`);

      const startTime = Date.now();
      const response = await authApi.verify2FA(tempToken, code, method);
      const duration = Date.now() - startTime;

      logger.log(`✅ [AUTH] 2FA verification successful in ${duration}ms`);

      // Store tokens
      await storeTokens({
        accessToken: response.tokens.access,
        refreshToken: response.tokens.refresh,
      });
      logger.log('✅ [AUTH] Tokens stored in SecureStore');

      // Store user data
      await platformStorage.setItem('user_data', JSON.stringify(response.user));
      logger.log('✅ [AUTH] User data cached');

      // Set user and clear 2FA pending flag
      setUser(response.user);
      setHasValidSession(true);
      setPending2FA(false);

      logger.log('✅ [AUTH] Authentication complete - user set, pending2FA cleared');
      logger.log('🔄 [AUTH] RootNavigator will detect user !== null and switch to Main stack');

      // Connect to WebSocket
      websocketService.connect(response.user.orgId, response.tokens.access);

      // Register for push notifications in background
      setTimeout(async () => {
        if (Platform.OS === 'web') {
          logger.log('Skipping push notifications on web platform');
          return;
        }

        // ❌ REMOVED: Duplicate registration call (already registered in initial login)
        // The push notification registration happens ONCE in the initial login flow
        // This duplicate call was causing 6x notifications
        logger.log('✅ 2FA verified - Push notifications already registered during login');
      }, 2000);

      logger.log('2FA verification complete - session started', {
        userId: response.user.id,
        orgId: response.user.orgId,
      });

      // ✅ Check for OTA updates after successful 2FA (silent background download)
      if (!__DEV__ && Platform.OS !== 'web' && Updates.isEnabled) {
        setTimeout(async () => {
          try {
            logger.log('🔍 [OTA] Checking for updates after 2FA...');
            const update = await Updates.checkForUpdateAsync();

            if (update.isAvailable) {
              logger.log('📥 OTA update available after 2FA, downloading...', {
                updateId: update.manifest?.id,
                timestamp: new Date().toISOString(),
              });

              await Updates.fetchUpdateAsync();
              logger.log('✅ OTA update downloaded after 2FA, will apply on next app restart');
              logger.log('✅ [OTA] Update downloaded successfully after 2FA');
            } else {
              logger.log('✅ [OTA] No updates available after 2FA');
            }
          } catch (otaError) {
            logger.warn('⚠️ [OTA] Failed to check updates after 2FA:', otaError);
            logger.warn('Failed to check OTA updates after 2FA', otaError);
          }
        }, 3000); // Wait 3 seconds after 2FA to not block UI
      }

      return { success: true };
    } catch (error: any) {
      logger.error('2FA verification failed:', error);
      throw new Error(error.response?.data?.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    logger.log('🚪 [AUTH] Logout initiated by user');

    // ✅ FIX: Remove FCM token from backend FIRST (prevents duplicate notifications)
    try {
      // Skip notifications on web platform
      if (Platform.OS !== 'web') {
        const { default: notificationService } = await import('../services/notificationService');
        notificationService.cleanupListeners();
        await notificationService.removePushToken();
        logger.log('✅ [AUTH] FCM token removed + listeners cleaned up');
      }
    } catch (notifError) {
      logger.warn('⚠️ [AUTH] Failed to remove push token (non-critical):', notifError);
      // Don't block logout if push token removal fails
    }

    // Check if we have a valid token before calling logout API
    const token = await platformStorage.getItem('access_token');

    if (token) {
      try {
        await authApi.logout();
        logger.log('✅ [AUTH] Logout API call successful');
      } catch (error: any) {
        // Only log error if it's not a 401 (token already invalid)
        if (error.response?.status !== 401) {
          logger.error('❌ [AUTH] Logout API failed:', error);
        } else {
          logger.log('ℹ️ [AUTH] Logout API returned 401 (token already invalid)');
        }
      }
    } else {
      logger.log('ℹ️ [AUTH] No token found, skipping logout API call');
    }

    // Always clean up, regardless of API call success
    // Clear token refresh timer
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      logger.log('🛑 [AUTH] Auto-refresh timer cleared');
    }

    // Disconnect WebSocket
    websocketService.disconnect();
    logger.log('🔌 [AUTH] WebSocket disconnected');

    // Clear ALL user data (tokens + cache + notification prefs) to prevent data leakage
    await clearAllUserData();
    logger.log('🗑️ [AUTH] All user data cleared from storage');

    // ✅ Reset 2FA pending flag
    setPending2FA(false);
    setHasValidSession(false); // ✅ Only here we truly invalidate the session
    setUser(null);

    logger.log('✅ [AUTH] Logout completed - session ended');
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const updateUser = async (updatedUser: Partial<User>) => {
    if (user) {
      const newUser = { ...user, ...updatedUser };
      setUser(newUser);
      // Update cached user data
      await platformStorage.setItem('user_data', JSON.stringify(newUser));
      logger.log('User data updated and cached', {
        userId: newUser.id,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        // ✅ FIX: isAuthenticated based on hasValidSession (tokens exist), not user state
        // This prevents logout when user state is temporarily null during API refresh
        isAuthenticated: hasValidSession && !pending2FA,
        isLoading,
        pending2FA,
        login,
        verify2FA, // NEW: 2FA verification method
        logout,
        checkAuth,
        refreshToken,
        updateUser,
        setPending2FA,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
